import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { MapPin, Calendar, Code2, Palette, Sparkles, Award, ExternalLink, Music, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { personalInfo as mockPersonalInfo, skills as mockSkills, experiences as mockExperiences, gallery as mockGallery } from '../data/mock';
import { useTheme } from '../context/ThemeContext';
import MouseFollower from '../components/MouseFollower';

const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const YoutubeIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

const SpotifyIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
  </svg>
);

const TwitterIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const GithubIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
  </svg>
);

const AboutPage = () => {
  const { themeColor, currentButtonShape } = useTheme();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [gallery, setGallery] = useState(mockGallery);
  const [personalInfo, setPersonalInfo] = useState(mockPersonalInfo);
  const [skills, setSkills] = useState(mockSkills);
  const [experiences, setExperiences] = useState(mockExperiences);

  useEffect(() => {
    Promise.all([
      fetch(`/api/admin/public/gallery`, { cache: 'no-store' }).then(r => r.json()).catch(() => null),
      fetch(`/api/admin/public/settings`, { cache: 'no-store' }).then(r => r.json()).catch(() => null),
      fetch(`/api/admin/public/skills`, { cache: 'no-store' }).then(r => r.json()).catch(() => null),
      fetch(`/api/admin/public/experiences`, { cache: 'no-store' }).then(r => r.json()).catch(() => null),
    ]).then(([galData, settingsData, skillsData, expData]) => {
      if (galData?.gallery?.length > 0) setGallery(galData.gallery);
      if (settingsData?.personalInfo) setPersonalInfo(prev => ({ ...prev, ...settingsData.personalInfo }));
      if (skillsData?.skills?.length > 0) setSkills(skillsData.skills);
      if (expData?.experiences?.length > 0) setExperiences(expData.experiences);
    });
  }, []);

  const socialLinks = [
    { name: 'Instagram', icon: InstagramIcon, href: personalInfo.socials?.instagram, color: '#E4405F' },
    { name: 'YouTube', icon: YoutubeIcon, href: personalInfo.socials?.youtube, color: '#FF0000' },
    { name: 'Spotify', icon: SpotifyIcon, href: personalInfo.socials?.spotify, color: '#1DB954' },
    { name: 'Twitter', icon: TwitterIcon, href: personalInfo.socials?.twitter, color: '#1DA1F2' },
    { name: 'GitHub', icon: GithubIcon, href: personalInfo.github, color: '#ffffff' },
  ];

  const highlights = [
    { icon: Code2, label: 'Yazılım Projeleri', value: '10+' },
    { icon: Palette, label: 'Grafik Tasarım', value: '20+' },
    { icon: Calendar, label: 'Yaş', value: new Date().getFullYear() - personalInfo.birthYear },
    { icon: Award, label: 'Deneyim', value: `${new Date().getFullYear() - personalInfo.experienceStart}+ Yıl` },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 relative">
      <MouseFollower />
      <div
        className="fixed top-0 right-0 w-1/2 h-full opacity-5 pointer-events-none"
        style={{ background: `radial-gradient(circle at top right, ${themeColor.primary}, transparent 70%)` }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <motion.div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 text-sm font-medium"
            style={{ backgroundColor: `rgba(${themeColor.rgb}, 0.1)`, color: themeColor.primary }}
          >
            <Sparkles size={16} />
            Hakkımda
          </motion.div>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">Beni Tanıyın</h1>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            Tutkulu bir geliştirici ve tasarımcı olarak dijital dünyada iz bırakmaya çalışıyorum
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left - Avatar & Social */}
          <motion.div
            className="lg:col-span-1"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="sticky top-28">
              <div className="relative mb-6">
                <motion.div className="absolute -inset-4 rounded-3xl opacity-20 blur-2xl" style={{ backgroundColor: themeColor.primary }} />
                <motion.div
                  className="relative w-full aspect-square rounded-3xl overflow-hidden border-2"
                  style={{ borderColor: themeColor.primary }}
                  whileHover={{ scale: 1.02 }}
                >
                  <img src={personalInfo.avatar} alt={personalInfo.name} className="w-full h-full object-cover" />
                </motion.div>
              </div>

              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-white mb-2">{personalInfo.fullName}</h2>
                <p style={{ color: themeColor.primary }} className="font-medium mb-4">{personalInfo.role}</p>
                <div className="flex flex-wrap justify-center gap-2 mb-6">
                  <span className="px-3 py-1 rounded-full bg-zinc-800/50 text-zinc-400 text-sm flex items-center gap-2">
                    <MapPin size={14} />{personalInfo.location}
                  </span>
                  <span className="px-3 py-1 rounded-full bg-green-500/10 text-green-400 text-sm flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                    İş için uygun
                  </span>
                </div>
              </div>

              {/* Music Site Button */}
              <motion.a
                href={personalInfo.musicSite}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 w-full px-5 py-3.5 font-medium text-white mb-4 transition-all border border-zinc-800/50 hover:border-zinc-600"
                style={{ background: `linear-gradient(135deg, rgba(${themeColor.rgb}, 0.15), rgba(${themeColor.rgb}, 0.05))`, borderRadius: currentButtonShape.radius }}
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                data-testid="music-site-btn"
              >
                <Music size={20} style={{ color: themeColor.primary }} />
                <span>Müzik Siteme Göz At</span>
                <ExternalLink size={16} className="text-zinc-500" />
              </motion.a>

              {/* Social Links */}
              <div className="p-4 rounded-3xl glass gradient-border" data-testid="social-links">
                <p className="text-xs uppercase tracking-wider text-zinc-500 font-medium mb-4 text-center">Sosyal Medya</p>
                <div className="grid grid-cols-3 gap-3">
                  {socialLinks.map((social) => (
                    <motion.a
                      key={social.name}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex flex-col items-center gap-2 p-3 rounded-2xl glass-hover transition-all group"
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      data-testid={`social-link-${social.name.toLowerCase()}`}
                    >
                      <div className="text-zinc-400 group-hover:text-white transition-colors">
                        <social.icon />
                      </div>
                      <span className="text-[10px] text-zinc-500 group-hover:text-zinc-300 transition-colors">{social.name}</span>
                    </motion.a>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right - Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Bio */}
            <motion.div
              className="p-6 md:p-8 rounded-3xl glass glass-hover gradient-border"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              data-testid="bio-section"
            >
              <h3 className="text-xl font-bold text-white mb-4">Hikayem</h3>
              <p className="text-zinc-300 leading-relaxed mb-4">{personalInfo.bio}</p>
              <p className="text-zinc-400 leading-relaxed">
                Grafik tasarım ve web geliştirme dünyasına olan tutkum, beni sürekli yeni teknolojiler öğrenmeye
                ve yaratıcı çözümler üretmeye yönlendiriyor. Her projemde kullanıcı deneyimini ön planda tutarak,
                modern ve işlevsel tasarımlar ortaya koymayı hedefliyorum.
              </p>
            </motion.div>

            {/* Stats */}
            <motion.div
              ref={ref}
              className="grid grid-cols-2 md:grid-cols-4 gap-4"
              variants={containerVariants}
              initial="hidden"
              animate={inView ? 'visible' : 'hidden'}
              data-testid="stats-section"
            >
              {highlights.map((item) => (
                <motion.div
                  key={item.label}
                  variants={itemVariants}
                  className="p-6 rounded-3xl glass glass-hover gradient-border text-center group"
                  whileHover={{ y: -5 }}
                >
                  <div
                    className="w-12 h-12 rounded-xl mx-auto mb-4 flex items-center justify-center transition-transform group-hover:scale-110"
                    style={{ backgroundColor: `rgba(${themeColor.rgb}, 0.1)` }}
                  >
                    <item.icon size={24} style={{ color: themeColor.primary }} />
                  </div>
                  <div className="text-3xl font-bold text-white mb-1">{item.value}</div>
                  <div className="text-sm text-zinc-500">{item.label}</div>
                </motion.div>
              ))}
            </motion.div>

            {/* Skills */}
            <motion.div
              className="p-6 md:p-8 rounded-3xl glass glass-hover gradient-border"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              data-testid="skills-section"
            >
              <h3 className="text-xl font-bold text-white mb-6">Yeteneklerim</h3>
              <div className="space-y-5">
                {skills.map((skill) => (
                  <div key={skill.name}>
                    <div className="flex justify-between mb-2">
                      <span className="text-white font-medium">{skill.name}</span>
                      <span style={{ color: themeColor.primary }}>{skill.percentage}%</span>
                    </div>
                    <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full rounded-full"
                        style={{ backgroundColor: themeColor.primary }}
                        initial={{ width: 0 }}
                        whileInView={{ width: `${skill.percentage}%` }}
                        transition={{ duration: 1 }}
                        viewport={{ once: true }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Experience - Detailed */}
            <motion.div
              className="p-6 md:p-8 rounded-3xl glass glass-hover gradient-border"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              data-testid="experience-section"
            >
              <h3 className="text-xl font-bold text-white mb-6">Deneyim</h3>
              <div className="space-y-8">
                {experiences.map((exp) => (
                  <div key={exp.id} className="relative pl-6 border-l-2 border-zinc-800">
                    <div
                      className="absolute left-0 top-0 w-3 h-3 rounded-full -translate-x-[7px]"
                      style={{ backgroundColor: themeColor.primary }}
                    />
                    <h4 className="text-white font-semibold text-lg">{exp.title}</h4>
                    <p className="text-zinc-500 text-sm mb-2">{exp.company} &bull; {exp.period}</p>
                    <p className="text-zinc-400 text-sm mb-3">{exp.description}</p>
                    {exp.details && (
                      <ul className="space-y-1.5">
                        {exp.details.map((detail, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-zinc-500">
                            <span className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: themeColor.primary }} />
                            {detail}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Gallery */}
            <motion.div
              className="p-6 md:p-8 rounded-3xl glass gradient-border"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              data-testid="gallery-section"
            >
              <h3 className="text-xl font-bold text-white mb-6">Galeri</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {gallery.map((item, index) => (
                  <motion.div
                    key={item.id}
                    className="relative aspect-square rounded-2xl overflow-hidden cursor-pointer group"
                    whileHover={{ scale: 1.03 }}
                    onClick={() => setLightboxIndex(index)}
                    data-testid={`gallery-item-${item.id}`}
                  >
                    <img src={item.image} alt={item.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-0 left-0 right-0 p-3">
                        <p className="text-white text-sm font-medium">{item.title}</p>
                        <p className="text-xs" style={{ color: themeColor.primary }}>{item.category}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIndex !== null && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/90 backdrop-blur-xl z-[100]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setLightboxIndex(null)}
            />
            <motion.div
              className="fixed inset-0 z-[101] flex items-center justify-center p-4 md:p-12"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
            >
              <button
                onClick={() => setLightboxIndex(null)}
                className="absolute top-4 right-4 p-2.5 rounded-xl bg-zinc-800/80 text-white hover:bg-zinc-700 z-10"
                data-testid="lightbox-close"
              >
                <X size={24} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setLightboxIndex((lightboxIndex - 1 + gallery.length) % gallery.length); }}
                className="absolute left-4 p-2.5 rounded-xl bg-zinc-800/80 text-white hover:bg-zinc-700 z-10"
                data-testid="lightbox-prev"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setLightboxIndex((lightboxIndex + 1) % gallery.length); }}
                className="absolute right-4 p-2.5 rounded-xl bg-zinc-800/80 text-white hover:bg-zinc-700 z-10"
                data-testid="lightbox-next"
              >
                <ChevronRight size={24} />
              </button>
              <img
                src={gallery[lightboxIndex].image}
                alt={gallery[lightboxIndex].title}
                className="max-w-full max-h-[80vh] rounded-2xl object-contain"
                onClick={(e) => e.stopPropagation()}
              />
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-center">
                <p className="text-white font-medium">{gallery[lightboxIndex].title}</p>
                <p className="text-sm text-zinc-400">{lightboxIndex + 1} / {gallery.length}</p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AboutPage;
